import { useState } from 'react'
import { FiX, FiClock, FiCalendar, FiAlertCircle, FiSend, FiUser } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { API_URL } from '../config/api'
import { formatearFecha } from '../utils/fechas'

const PropuestaMarcajeModal = ({ diasSeleccionados, onClose, onSuccess, buildApiUrl }) => {
  const [motivo, setMotivo] = useState('')
  const [horaEntrada, setHoraEntrada] = useState('')
  const [horaSalida, setHoraSalida] = useState('')
  const [loading, setLoading] = useState(false)
  const [progreso, setProgreso] = useState({ actual: 0, total: 0 })

  const apiUrl = buildApiUrl ? buildApiUrl('/correcciones/proponer') : `${API_URL}/correcciones/proponer`
  const esMasivo = diasSeleccionados.length > 1

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!motivo.trim()) {
      toast.error('Debes indicar el motivo de la propuesta')
      return
    }

    if (!horaEntrada) {
      toast.error('Debes indicar la hora de entrada')
      return
    }

    setLoading(true)
    setProgreso({ actual: 0, total: diasSeleccionados.length })
    const token = localStorage.getItem('token')

    let exitos = 0
    const fallos = []

    for (const dia of diasSeleccionados) {
      setProgreso(prev => ({ ...prev, actual: prev.actual + 1 }))
      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            empleado_id: dia.empleado_id,
            fecha: dia.fecha,
            hora_entrada_corregida: horaEntrada,
            hora_salida_corregida: horaSalida || null,
            motivo
          })
        })

        if (!response.ok) {
          const data = await response.json()
          throw new Error(data.error || 'Error al enviar propuesta')
        }
        exitos++
      } catch (error) {
        fallos.push(`${dia.empleado_nombre} - ${formatearFecha(dia.fecha)}: ${error.message}`)
      }
    }

    setLoading(false)

    if (exitos > 0 && fallos.length === 0) {
      toast.success(exitos === 1
        ? 'Propuesta de marcaje enviada al empleado'
        : `${exitos} propuestas de marcaje enviadas`
      )
      onSuccess()
    } else if (exitos > 0) {
      toast.success(`${exitos} de ${diasSeleccionados.length} propuestas enviadas`)
      toast.error(`Fallaron: ${fallos.join('; ')}`, { duration: 8000 })
      onSuccess()
    } else {
      toast.error(fallos.length === 1 ? fallos[0] : 'Error al enviar propuestas')
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="card-glass max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200/50">
          <h3 className="text-lg font-semibold text-gray-900">
            Proponer Marcaje
            {esMasivo && (
              <span className="ml-2 badge badge-primary">{diasSeleccionados.length} días</span>
            )}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Info de días seleccionados */}
        <div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200/50">
          {esMasivo ? (
            <div className="max-h-40 overflow-y-auto space-y-1">
              {diasSeleccionados.map((dia) => (
                <div key={dia.id} className="flex items-center gap-2 text-sm text-gray-600">
                  <FiUser size={14} className="flex-shrink-0" />
                  <span className="font-medium">{dia.empleado_nombre}</span>
                  <span className="text-gray-400">—</span>
                  <FiCalendar size={14} className="flex-shrink-0" />
                  <span>{formatearFecha(dia.fecha)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <FiUser size={16} />
              <span className="font-medium">{diasSeleccionados[0].empleado_nombre}</span>
              <span>—</span>
              <FiCalendar size={16} />
              <span>{formatearFecha(diasSeleccionados[0].fecha)}</span>
            </div>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Hora Entrada */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hora de entrada propuesta *
            </label>
            <div className="relative">
              <FiClock className="absolute left-3 top-1/2 -translate-y-1/2 text-green-500" size={16} />
              <input
                type="time"
                value={horaEntrada}
                onChange={(e) => setHoraEntrada(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Hora Salida */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hora de salida propuesta
            </label>
            <div className="relative">
              <FiClock className="absolute left-3 top-1/2 -translate-y-1/2 text-red-500" size={16} />
              <input
                type="time"
                value={horaSalida}
                onChange={(e) => setHoraSalida(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              />
            </div>
          </div>

          {/* Motivo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Motivo de la propuesta *
            </label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Explica al empleado por qué propones este marcaje..."
              required
              disabled={loading}
            />
          </div>

          {/* Info */}
          <div className="flex items-start gap-2 p-3 bg-orange-50 rounded-md border border-orange-200">
            <FiAlertCircle className="text-orange-600 mt-0.5 flex-shrink-0" size={16} />
            <p className="text-sm text-orange-800">
              {esMasivo
                ? 'Se enviará una propuesta individual a cada empleado. El fichaje solo se creará cuando el empleado la acepte.'
                : 'La propuesta será enviada al empleado para su aprobación. Si la acepta, se creará un fichaje para ese día.'
              }
            </p>
          </div>

          {/* Progreso */}
          {loading && esMasivo && (
            <div className="text-center text-sm text-blue-600 font-medium">
              Enviando {progreso.actual} de {progreso.total}...
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary flex-1"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-ripple flex-1 flex items-center justify-center gap-2"
              disabled={loading}
            >
              <FiSend size={16} />
              {loading
                ? 'Enviando...'
                : esMasivo
                  ? `Enviar ${diasSeleccionados.length} propuestas`
                  : 'Enviar Propuesta'
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default PropuestaMarcajeModal
